import { coreActions } from '@sonar-software/sonar-core';
import { FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { GatewayContent } from '../general';
import { LocalStorageKey } from '@sonar-software/sonar-ts-types';
import { SyncOrganizationView } from './SyncOrganizationView';

import { ConfirmOrganizationView, OnboardingErrorView } from './general';
import { isSyncCompleted, isSyncFailed, isSyncInProgress } from '@sonar-software/sonar-ts-lib';
import { Navigate, Route, Routes } from 'react-router-dom';
import React, { useCallback } from 'react';
import {
	useAction,
	useAuthToken,
	useCurrentOrganization,
	useLatestSalesforceSync,
	useOnboardingProgress,
	useSalesforceOrganizations,
	useSwitchOrganization
} from '@hooks';

export const OrganizationView: React.FC = () => {
	const organizations = useSalesforceOrganizations();
	const { organizationId } = useAuthToken();
	const latestSync = useLatestSalesforceSync();
	const currentOrganization = useCurrentOrganization();
	const { loading, progress, updateProgress } = useOnboardingProgress();

	const switchOrganization = useSwitchOrganization();
	const unlinkOrganization = useAction(coreActions.requestPostCoreOrganizationsUnlink);

	const handleRestart = useCallback(() => {
		const lastUsedOrgId = localStorage.getItem(LocalStorageKey.LastUsedOrg);

		if (organizationId && organizationId !== progress?.organizationId) {
			unlinkOrganization({ organizationId });
		}

		let targetOrganizationId = 0;

		if (lastUsedOrgId && !isNaN(Number(lastUsedOrgId))) {
			targetOrganizationId = Number(lastUsedOrgId);
			localStorage.removeItem(LocalStorageKey.LastUsedOrg);
		} else {
			targetOrganizationId = organizations[ 0 ]?.id ?? progress?.organizationId;
		}

		switchOrganization({ organizationId: targetOrganizationId }, () => {
			updateProgress({
				isSalesforceConnected: true,
				isSalesforceSynced: true
			});
		});
	}, [
		organizationId,
		organizations,
		progress?.organizationId,
		switchOrganization,
		unlinkOrganization,
		updateProgress
	]);

	if (loading || !progress) {
		return <GatewayContent />;
	}

	const hasCurrentRunningImport = isSyncInProgress(latestSync?.status);
	const hasSuccessfulImport = isSyncCompleted(latestSync?.status);
	const hasFailedImport = isSyncFailed(latestSync?.status);

	if (hasFailedImport && !currentOrganization?.hasSalesforceData) {
		return <OnboardingErrorView onRestart={ handleRestart } />;
	}

	const {	missingSalesforceSyncPermission } = progress;

	const showConfirm = missingSalesforceSyncPermission ||
		(!currentOrganization?.hasSalesforceData && !hasCurrentRunningImport && !hasSuccessfulImport);

	const showSync = !currentOrganization?.hasSalesforceData &&
		(hasCurrentRunningImport || hasSuccessfulImport);

	if (!showConfirm && !showSync) {
		return <GatewayContent />;
	}

	return (
		<Routes>
			{ showConfirm && <Route
				element={ <ConfirmOrganizationView onRestart={ handleRestart } /> }
				path={ FULL_ROUTES.ORGANIZATION_CONFIRM } /> }
			{ showConfirm && <Route
				element={ <Navigate to={ FULL_ROUTES.ORGANIZATION_CONFIRM } /> }
				path='*' /> }
			{ showSync && <Route
				element={ <SyncOrganizationView /> }
				path={ FULL_ROUTES.ORGANIZATION_SYNC } /> }
			{ showSync && <Route
				element={ <Navigate to={ FULL_ROUTES.ORGANIZATION_SYNC } /> }
				path='*' /> }
		</Routes>
	);
};
